import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../context/JourneyContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, ArrowRight, Sparkles, CheckCircle2, BookOpen } from 'lucide-react';

const PATHWAY_DETAILS = {
  'Building Confidence Across the Whole School': {
    headline: 'Building Confidence Across the Whole School',
    outcomes: [
      'Unite your school through joyful movement',
      'Build confidence across all year levels',
      'Create lasting memories through celebration'
    ],
    nextSteps: 'We\'ll discuss your school\'s needs, timing, and how to make this experience unforgettable for your tamariki.'
  },
  'Structured, Systemised Movement Delivery': {
    headline: 'Structured, Systemised Movement Delivery',
    outcomes: [
      'Confidence development',
      'Engagement and attendance support',
      'Leadership building',
      'Performance excellence'
    ],
    nextSteps: 'Let\'s start a conversation about creating a structure and system tailored to your school\'s specific needs.'
  },
  'Leadership & Wellbeing Programs': {
    headline: 'Leadership & Wellbeing',
    outcomes: [
      'Develop leadership through movement',
      'Build confidence in facilitation',
      'Experience wellbeing-focused sessions'
    ],
    nextSteps: 'Let\'s discuss the right option for your context — we also offer koha-based PL sessions for teachers and senior leaders.'
  },
  'Workplace Wellbeing Sessions': {
    headline: 'Workplace Wellbeing Sessions',
    outcomes: [
      'Team cohesion',
      'Confidence and communication',
      'Movement-based wellbeing'
    ],
    nextSteps: 'Book a 60-90 minute session to bring movement and wellbeing to your workplace.'
  },
  'Strategic Partnerships': {
    headline: 'Strategic Partnerships',
    outcomes: [
      'Multi-year aligned collaborations',
      'Support schools and communities',
      'Create meaningful impact together'
    ],
    nextSteps: 'Partner with us to fund movement and wellbeing initiatives in schools.'
  }
};

const RecommendationScreen = () => {
  const navigate = useNavigate();
  const { journey, trackPageView, trackCtaClick } = useJourney();

  React.useEffect(() => {
    trackPageView('recommendation');
  }, [trackPageView]);

  const pathway = journey?.recommended_pathway || 'Building Confidence Across the Whole School';
  const portal = journey?.recommended_portal || 'Primary & Intermediate Programs';
  const cta = journey?.recommended_cta || 'Enquire Now';
  const details = PATHWAY_DETAILS[pathway] || PATHWAY_DETAILS['Building Confidence Across the Whole School'];

  const handlePrimaryCta = () => {
    trackCtaClick(cta);
    navigate('/portal/your-need');
  };

  const handleExploreResources = () => {
    trackCtaClick('explore_resources');
    navigate('/ls-plus');
  };

  const handleBack = () => {
    navigate('/choose-path');
  };

  return (
    <div 
      data-testid="recommendation-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background */}
      <div className="absolute top-40 -right-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        {/* Back button */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={handleBack}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Recommendation Card */}
        <Card className="bg-stone-900/80 border border-stone-800 rounded-3xl p-8 md:p-12 animate-fade-in-up">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <p className="text-primary font-medium">Your Pathway</p>
          </div>

          {/* Pathway name */}
          <h1 
            data-testid="pathway-name"
            className="text-3xl sm:text-4xl font-heading font-bold text-white mb-2"
          >
            {details.headline}
          </h1>
          
          <p className="text-stone-400 mb-8">
            Part of our <span className="text-accent font-medium">{portal}</span>
          </p>

          {/* Outcomes */}
          <div className="space-y-4 mb-8">
            <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider">
              What you can expect
            </h3>
            {details.outcomes.map((outcome, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 animate-slide-in-right opacity-0"
                style={{ animationDelay: `${0.2 + index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <p className="text-stone-300">{outcome}</p>
              </div>
            ))}
          </div>

          {/* What happens next */}
          <div className="bg-stone-800/50 rounded-2xl p-6 mb-8 border border-stone-700">
            <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">
              What usually happens next
            </h3>
            <p className="text-stone-300">
              {details.nextSteps}
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="primary-cta-btn"
              onClick={handlePrimaryCta}
              size="lg"
              className="rounded-full px-8 py-6 text-lg font-bold bg-primary hover:bg-primary/90 text-white transition-all duration-300 hover:scale-105 active:scale-95 flex-1"
            >
              {cta}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <Button
              data-testid="explore-resources-btn"
              onClick={handleExploreResources}
              variant="outline"
              size="lg"
              className="rounded-full px-8 py-6 text-lg font-medium border-stone-700 text-stone-300 hover:border-primary/50 hover:bg-stone-800 transition-all duration-300"
            >
              <BookOpen className="mr-2 w-5 h-5" />
              Explore LS+
            </Button>
          </div>
        </Card>

        {/* Journey summary */}
        {journey && (
          <div className="mt-8 text-center text-sm text-stone-500 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <p>
              Selected: <span className="text-stone-300">{journey.user_type}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationScreen;
